import React, { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, XCircle, Clock } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { esitoAccessoBadge } from "@/components/accessi/utils";

export const StoricoAccessi = ({ eventi, gruppi }) => {
  const { can } = useAuth();
  const manage = can("accessi.manage");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evento, setEvento] = useState("tutti");
  const [gruppo, setGruppo] = useState("tutti");
  const [esito, setEsito] = useState("tutti");
  const [da, setDa] = useState("");
  const [a, setA] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (evento !== "tutti") params.evento_id = evento;
      if (gruppo !== "tutti") params.gruppo = gruppo;
      if (esito !== "tutti") params.esito = esito;
      if (da) params.da = da;
      if (a) params.a = a;
      if (q) params.q = q;
      const { data } = await api.get("/accessi", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(extractError(e)); } finally { setLoading(false); }
  }, [evento, gruppo, esito, da, a, q]);
  useEffect(() => { load(); }, [load]);

  const annulla = async (acc) => {
    if (!window.confirm("Annullare questa presenza? Non sarà conteggiata.")) return;
    try { await api.delete(`/accessi/${acc.id}`); toast.success("Presenza annullata"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <div className="lg:col-span-2">
          <Label className="text-xs">Evento</Label>
          <Select value={evento} onValueChange={setEvento}>
            <SelectTrigger data-testid="storico-evento-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti gli eventi</SelectItem>
              {eventi.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome_evento}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Dal</Label><Input type="date" value={da} onChange={(e) => setDa(e.target.value)} data-testid="storico-da" /></div>
        <div><Label className="text-xs">Al</Label><Input type="date" value={a} onChange={(e) => setA(e.target.value)} data-testid="storico-a" /></div>
        <div>
          <Label className="text-xs">Gruppo</Label>
          <Select value={gruppo} onValueChange={setGruppo}>
            <SelectTrigger data-testid="storico-gruppo-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              {gruppi.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Esito</Label>
          <Select value={esito} onValueChange={setEsito}>
            <SelectTrigger data-testid="storico-esito-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              <SelectItem value="confermato">Confermato</SelectItem>
              <SelectItem value="sospeso">Sospeso</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o codice fiscale" className="pl-9" data-testid="storico-search" />
      </div>

      {loading ? <LoadingState /> : (items.length === 0 ? (
        <EmptyState title="Nessun accesso" description="Non ci sono accessi per i filtri selezionati." />
      ) : (
        <Card className="divide-y overflow-hidden" data-testid="storico-list">
          {items.map((acc) => {
            const b = esitoAccessoBadge(acc.esito, acc.annullato);
            return (
              <div key={acc.id} className="p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium truncate">{acc.nome_completo || `${acc.cognome || ""} ${acc.nome || ""}`}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {acc.codice_fiscale || "CF n/d"}{acc.gruppo ? ` · ${acc.gruppo}` : ""} · {acc.evento_nome || "-"}
                  </div>
                  <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" /> {fmtDateTime(acc.data_ora)}{acc.operatore_nome ? ` · ${acc.operatore_nome}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>
                  {manage && !acc.annullato && (
                    <Button size="sm" variant="outline" onClick={() => annulla(acc)} data-testid={`storico-annulla-${acc.id}`}>
                      <XCircle className="h-4 w-4 mr-1" /> Annulla
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </Card>
      ))}
      <p className="text-xs text-muted-foreground mt-3">Totale righe: {items.length}</p>
    </div>
  );
};
